import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { ReadFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { WriteFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';

type HookStatus = 'loading' | 'done' | 'error' | 'saving';
type ReloadEntityFn = () => void;
type SaveEntityFn<TRead, TWrite> = (
  updateRequest: TWrite,
  onComplete?: (newEntity: TRead) => void,
  onError?: (error: BaseError) => void,
) => void;

interface ReadWritePatternLoadingHook {
  status: 'loading';
  saveIndicatorStatus: 'idle';
  canWrite: false;
  saved: false;
  entity: null;
}

interface ReadWritePatternDoneHook<TRead, TWrite> {
  status: 'done';
  saved: boolean;
  entity: TRead;
  saveEntity: SaveEntityFn<TRead, TWrite>;
  reloadEntity: ReloadEntityFn;
  saveIndicatorStatus: 'idle' | 'saved';
  canWrite: boolean;
}

interface ReadWritePatternErrorHook<TRead, TWrite> {
  status: 'error';
  entity: TRead | null;
  error: BaseError;
  saveEntity: SaveEntityFn<TRead, TWrite>;
  reloadEntity: ReloadEntityFn;
  saveIndicatorStatus: 'idle';
  canWrite: boolean;
  saved: false;
}

interface ReadWritePatternSavingHook<TWrite> {
  status: 'saving';
  updateRequest: TWrite;
  saveIndicatorStatus: 'saving';
  canWrite: false;
  saved: false;
  entity: null;
}

export type ReadWritePatternHook<TRead, TWrite> =
  | ReadWritePatternLoadingHook
  | ReadWritePatternDoneHook<TRead, TWrite>
  | ReadWritePatternSavingHook<TWrite>
  | ReadWritePatternErrorHook<TRead, TWrite>;

interface SaveTransaction<TWrite> {
  updateRequest: TWrite;
}

export const useReadWritePattern = <TRead, TWrite>({
  isReadyToRead,
  readFn,
  isReadyToWrite,
  writeFn,
}: {
  isReadyToRead: boolean;
  readFn: ReadFn<TRead>;
  isReadyToWrite: boolean;
  writeFn: WriteFn<TRead, TWrite>;
}): ReadWritePatternHook<TRead, TWrite> => {
  const entity = useRef<TRead | null>(null);
  const saved = useRef<boolean>(false);
  const [status, setStatus] = useState<HookStatus>('loading');
  const [error, setError] = useState<BaseError | null>(null);
  const saveTransaction = useRef<SaveTransaction<TWrite> | null>(null);

  const transitionToError = useCallback((error: BaseError) => {
    saveTransaction.current = null;
    setStatus('error');
    setError(error);
  }, []);

  const transitionToDone = useCallback(
    (loadedEntity: TRead, wasSaved: boolean) => {
      saveTransaction.current = null;
      saved.current = wasSaved;
      entity.current = loadedEntity;
      setStatus('done');
    },
    [],
  );

  const transitionToSaving = useCallback((updateRequest: TWrite) => {
    saveTransaction.current = { updateRequest };
    setStatus('saving');
  }, []);

  const transitionToLoading = useCallback(() => {
    saveTransaction.current = null;
    entity.current = null;
    setStatus('loading');
  }, []);

  const loadEntity = useCallback(
    (wasSaved: boolean) => {
      if (!isReadyToRead) {
        return;
      }

      transitionToLoading();
      readFn(
        (result) => {
          transitionToDone(result, wasSaved);
        },
        (error) => {
          transitionToError(error);
        },
      );
    },
    [
      isReadyToRead,
      readFn,
      transitionToDone,
      transitionToError,
      transitionToLoading,
    ],
  );

  const saveEntity = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      updateRequest: TWrite,
      onComplete?: (newEntity: TRead) => void,
      onError?: (error: BaseError) => void,
    ) => {
      if (!isAlive() || !isReadyToWrite || saveTransaction.current) {
        return;
      }

      transitionToSaving(updateRequest);

      writeFn(
        updateRequest,
        (writtenEntity) => {
          if (!isAlive()) {
            return;
          }

          transitionToDone(writtenEntity, true);

          if (onComplete) {
            onComplete(writtenEntity);
          }
        },
        (error) => {
          if (!isAlive()) {
            return;
          }

          transitionToError(error);

          if (onError) {
            onError(error);
          }
        },
      );
    },
    [
      isReadyToWrite,
      transitionToDone,
      transitionToError,
      transitionToSaving,
      writeFn,
    ],
  );

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || !isReadyToRead || status !== 'loading') {
        return;
      }

      loadEntity(false);
    },
    [isReadyToRead, loadEntity, status],
  );

  const reloadEntity = useCallback(() => {
    if (status !== 'done' && status !== 'error') {
      return;
    }

    loadEntity(false);
  }, [loadEntity, status]);

  return useMemo(() => {
    if (status === 'loading') {
      return {
        status,
        saveIndicatorStatus: 'idle',
        canWrite: false,
        saved: false,
        entity: null,
      };
    }

    if (status === 'error') {
      return {
        status,
        entity: entity.current,
        error: error ?? {
          errorType: 'UnknownError',
          errorReason: 'Unknown error',
        },
        saveEntity,
        reloadEntity,
        saveIndicatorStatus: 'idle',
        canWrite: isReadyToWrite,
        saved: false,
      };
    }

    if (status === 'saving') {
      if (!saveTransaction.current) {
        throw new Error('saveTransaction.current is required');
      }

      return {
        status,
        updateRequest: saveTransaction.current.updateRequest,
        saveIndicatorStatus: 'saving',
        canWrite: false,
        saved: false,
        entity: null,
      };
    }

    if (status === 'done') {
      if (!entity.current) {
        throw new Error('entity.current is required');
      }

      return {
        status,
        entity: entity.current,
        saved: saved.current,
        saveIndicatorStatus: saved.current ? 'saved' : 'idle',
        reloadEntity,
        saveEntity,
        canWrite: isReadyToWrite,
      };
    }

    throw guardExhaustive(status);
  }, [error, isReadyToWrite, reloadEntity, saveEntity, status]);
};
